import {
  NotificationsApi,
  NotificationsEnabled,
  NotificationsSchedule,
  PublisherUpdatesModeMode,
  UpdateType,
} from 'cmp-client-api';

import { Topic } from '@src/domain/pages/notification-settings/publisher-updates/types';
import { NotificationServiceInterface } from '@src/server/services/types/notification.service.interface';

export class NotificationService implements NotificationServiceInterface {
  private readonly service: NotificationsApi;

  public constructor(service: NotificationsApi) {
    this.service = service;
  }

  public async getSubscriptions(serviceName: string): Promise<Topic[]> {
    return this.service
      .notificationsSubscriptionsServiceNameGet(serviceName)
      .then(({ data }) => data);
  }

  public async getPublisherUpdatesSubscriptions(): Promise<Topic[]> {
    return this.service.notificationsSubscriptionsPublisherUpdatesGet().then(({ data }) => data);
  }

  public async getPublisherUpdatesMode(): Promise<PublisherUpdatesModeMode> {
    return this.service
      .notificationsSubscriptionsPublisherUpdatesModeGet()
      .then(({ data }) => data.mode);
  }

  public async setPublisherUpdatesMode(mode: PublisherUpdatesModeMode): Promise<void> {
    await this.service.notificationsSubscriptionsPublisherUpdatesModePut({ mode });
  }

  public async getTopics(serviceName: string): Promise<Topic[]> {
    return this.service
      .notificationsServicesServiceNameTopicsGet(serviceName)
      .then(({ data }) => data);
  }

  public async addSubscription(topicIds: string[]): Promise<void> {
    return this.service.notificationsSubscriptionsPost(topicIds).then(({ data }) => data);
  }

  public async removeSubscription(topicIds: string[]): Promise<void> {
    return this.service.notificationsSubscriptionsDelete(topicIds).then(({ data }) => data);
  }

  public async getUpdateTypes(): Promise<UpdateType[]> {
    return this.service
      .notificationsSubscriptionsUpdateTypesGet()
      .then(({ data }) => data.filter((type) => Object.values(UpdateType).includes(type)));
  }

  public async modifyUpdateTypes(payload: UpdateType[]): Promise<void> {
    await this.service.notificationsSubscriptionsUpdateTypesPut(payload);
  }

  public async getSchedule(): Promise<NotificationsSchedule> {
    return this.service.notificationsSubscriptionsScheduleGet().then(({ data }) => data);
  }

  public async updateSchedule(
    payload: NotificationsSchedule,
    timezone: string = 'UNC'
  ): Promise<void> {
    await this.service.notificationsSubscriptionsSchedulePut(payload, timezone);
  }

  public async getNotificationsEnabled(): Promise<NotificationsEnabled> {
    return this.service
      .notificationsSubscriptionsNotificationsEnabledGet()
      .then(({ data }) => data);
  }

  public async updateNotificationsEnabled(payload: NotificationsEnabled): Promise<void> {
    await this.service.notificationsSubscriptionsNotificationsEnabledPut(payload);
  }

  public async resetNotificationSettings(timezone: string = 'UNC'): Promise<void> {
    return this.service
      .notificationsSubscriptionsDefaultSettingsPut(timezone)
      .then(({ data }) => data);
  }
}
